import {
  convertEventSelectToTarget,
  DataTable,
  formatDataToLabelValue,
  FormFeedBack,
  ModalConfirmAction,
  SelectRemote,
  STATUSPAGE,
} from '@repo/ui';
import {
  AddPlaceToGroupUseCaseInterface,
  GetAllPlacesFromGroupUseCaseInterface,
  GetInternshipLocationUseCaseInterface,
  ImportPlacesFromAnotherGroupUseCaseInterface,
  RemovePlaceFromGroupUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSelectedGroupInternshipLocationsListData,
  loadSelectedGroupInternshipLocationsListData,
} from 'presentation/config/store/groups/group-settings-slice';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, CardBody, Col, Form, Row, Spinner } from 'reactstrap';
import * as Yup from 'yup';

import noDataBg from '../../../../../assets/images/no-task.png';
import { makeColumnsGroupSettingsInternshipLocationsList } from '../../../common/columns/group-settings/columns-group-settings-internship-locations-list';
import { ImportEntitiesFromAnotherGroupModal } from '../import-entities-from-another-group-modal';

interface IProps {
  groupId: string;
}

const statusInitialStates = {
  confirmRemovalFromGroupModalBtnsStatus: STATUSPAGE.DONE,
  importInternshipLocationsFromAnotherGroupModalBtnsStatus: STATUSPAGE.DONE,
  internshipLocationRemoteSelectInputStatus: STATUSPAGE.DONE,
  addInternshipLocationToGroupBtnStatus: STATUSPAGE.DONE,
  internshipLocationsFromGroupListStatus: STATUSPAGE.DONE,
};

export const InternshipLocationsTabContent = ({ groupId }: IProps) => {
  // Hooks
  const { internshipLocationService, groupService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();

  // States
  const [
    isConfirmRemovalFromGroupModalOpen,
    setIsConfirmRemovalFromGroupModalOpen,
  ] = useState(false);
  const [
    isImportInternshipLocationsFromAnotherGroupModalOpen,
    setIsImportInternshipLocationsFromAnotherGroupModalOpen,
  ] = useState(false);
  const [
    selectedInternshipLocationFromGroupRelationId,
    setSelectedInternshipLocationFromGroupRelationId,
  ] = useState<string | null>(null);
  const [statusStates, setStatusStates] = useState(statusInitialStates);
  const { selectedGroupInternshipLocationsListData } = useAppSelector(
    (state) => state.groupSettings,
  );
  const [internshipLocationSelectOptions, setInternshipLocationSelectOptions] =
    useState<GetInternshipLocationUseCaseInterface.Result>([]);

  // Methods
  const changeStatusState = useCallback(
    <T extends keyof typeof statusInitialStates>(key: T, value: STATUSPAGE) => {
      setStatusStates((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const toggleConfirmRemovalFromGroupModal = useCallback(
    () => setIsConfirmRemovalFromGroupModalOpen((prevState) => !prevState),
    [],
  );

  const toggleImportInternshipLocationsFromAnotherGroupModal = useCallback(
    () =>
      setIsImportInternshipLocationsFromAnotherGroupModalOpen(
        (prevState) => !prevState,
      ),
    [],
  );

  const requestGetAllInternshipLocationsFromGroup = useCallback(
    async (params: GetAllPlacesFromGroupUseCaseInterface.Params) => {
      changeStatusState(
        'internshipLocationsFromGroupListStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .getAllPlacesFromGroup(params)
        .then((res) =>
          dispatch(loadSelectedGroupInternshipLocationsListData(res)),
        )
        .catch((err) => {
          notifyError(err.message);
          dispatch(clearSelectedGroupInternshipLocationsListData());
        })
        .finally(() =>
          changeStatusState(
            'internshipLocationsFromGroupListStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestGetAllInternshipLocationsForSelectOptions = useCallback(
    async (params: GetInternshipLocationUseCaseInterface.Params) => {
      changeStatusState(
        'internshipLocationRemoteSelectInputStatus',
        STATUSPAGE.LOADING,
      );
      await internshipLocationService
        .getInternshipLocation(params)
        .then((res) => setInternshipLocationSelectOptions(res))
        .catch((err) => {
          notifyError(err.message);
          setInternshipLocationSelectOptions([]);
        })
        .finally(() =>
          changeStatusState(
            'internshipLocationRemoteSelectInputStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestAddInternshipLocationToGroup = useCallback(
    async (params: AddPlaceToGroupUseCaseInterface.Params) => {
      changeStatusState(
        'addInternshipLocationToGroupBtnStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .addPlaceToGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllInternshipLocationsFromGroup({
            groupId,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'addInternshipLocationToGroupBtnStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestRemoveInternshipLocationFromGroup = useCallback(
    async (params: RemovePlaceFromGroupUseCaseInterface.Params) => {
      changeStatusState(
        'confirmRemovalFromGroupModalBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .removePlaceFromGroup(params)
        .then((res) => notifySuccess(res.message))
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'confirmRemovalFromGroupModalBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestImportInternshipLocationsFromAnotherGroup = useCallback(
    async (params: ImportPlacesFromAnotherGroupUseCaseInterface.Params) => {
      changeStatusState(
        'importInternshipLocationsFromAnotherGroupModalBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .importPlacesFromAnotherGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllInternshipLocationsFromGroup({
            groupId,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'importInternshipLocationsFromAnotherGroupModalBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      internshipLocation: null,
    },

    validationSchema: Yup.object({
      internshipLocation: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Campo é obrigatório'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        id: values.internshipLocation?.value,
        group: groupId,
      };

      requestAddInternshipLocationToGroup(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetAllInternshipLocationsFromGroup({
      groupId,
    });
  }, [groupId]);

  return (
    <>
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <CardBody>
          <Row>
            <Col lg={7} className="mb-3">
              <SelectRemote
                isClearable
                name="internshipLocation"
                clearOptions={() => setInternshipLocationSelectOptions(null)}
                functionSearch={(searchString) => {
                  if (searchString.length === 0) {
                    setInternshipLocationSelectOptions(null);
                    return;
                  }

                  if (searchString.length < 3) return;

                  requestGetAllInternshipLocationsForSelectOptions({
                    query: searchString,
                  });
                }}
                inputId={'internshipLocation'}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('internshipLocation', e),
                  );
                }}
                onBlur={(e) => {
                  setInternshipLocationSelectOptions(null);
                }}
                options={formatDataToLabelValue(
                  internshipLocationSelectOptions as never,
                  'id',
                  'name',
                )}
                placeholder={`Selecione um ${getLegends('LOCAL_ESTAGIO')}`}
                value={validation.values.internshipLocation}
                loading={
                  statusStates.internshipLocationRemoteSelectInputStatus ===
                  STATUSPAGE.LOADING
                }
                isInvalid={
                  !!(
                    validation.touched?.internshipLocation &&
                    validation.errors?.internshipLocation
                  )
                }
                isDisabled={
                  statusStates.addInternshipLocationToGroupBtnStatus ===
                  STATUSPAGE.LOADING
                }
              />
              <FormFeedBack
                error={String(validation.errors?.internshipLocation)}
                isInvalid={
                  !!(
                    validation.touched?.internshipLocation &&
                    validation.errors?.internshipLocation
                  )
                }
              />
            </Col>
            <Col lg={5} className="mb-3">
              <div className="d-flex justify-content-end gap-4">
                <Button
                  type="submit"
                  disabled={
                    statusStates.addInternshipLocationToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  {statusStates.addInternshipLocationToGroupBtnStatus ===
                    STATUSPAGE.LOADING && (
                    <Spinner size="sm">{['Carregando...']}</Spinner>
                  )}
                  Adicionar
                </Button>
                <Button
                  type="button"
                  onClick={() =>
                    toggleImportInternshipLocationsFromAnotherGroupModal()
                  }
                  disabled={
                    statusStates.addInternshipLocationToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  Importar de outro {getLegends('GRUPO')}
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <DataTable
              noDataBg={noDataBg}
              actions={[
                {
                  id: 1,
                  name: 'remove',
                  label: 'Remover',
                  icon: 'ri-indeterminate-circle-fill',
                  classNameColorIcon: 'danger',
                  onPress: (cellProps) => {
                    setSelectedInternshipLocationFromGroupRelationId(
                      cellProps.relationId,
                    );
                    toggleConfirmRemovalFromGroupModal();
                  },
                },
              ]}
              canSearch
              columns={makeColumnsGroupSettingsInternshipLocationsList()}
              data={selectedGroupInternshipLocationsListData}
              loading={
                statusStates.internshipLocationsFromGroupListStatus ===
                STATUSPAGE.LOADING
              }
            />
          </Row>
        </CardBody>
      </Form>

      <ModalConfirmAction
        modalTheme="danger"
        title={`Deseja remover o ${getLegends('LOCAL_ESTAGIO')} do grupo?`}
        actionBtnLabel="Remover"
        isOpen={isConfirmRemovalFromGroupModalOpen}
        toggle={toggleConfirmRemovalFromGroupModal}
        onConfirm={() =>
          requestRemoveInternshipLocationFromGroup({
            relationId: selectedInternshipLocationFromGroupRelationId,
          })
        }
        loading={
          statusStates.confirmRemovalFromGroupModalBtnsStatus ===
          STATUSPAGE.LOADING
        }
      />

      <ImportEntitiesFromAnotherGroupModal
        isOpen={isImportInternshipLocationsFromAnotherGroupModalOpen}
        toggle={toggleImportInternshipLocationsFromAnotherGroupModal}
        importedEntitiesTitle={getLegends('LOCAL_ESTAGIO', true)}
        buttonsLoading={
          statusStates.importInternshipLocationsFromAnotherGroupModalBtnsStatus ===
          STATUSPAGE.LOADING
        }
        requestImportEntitiesFromAnotherGroup={
          requestImportInternshipLocationsFromAnotherGroup
        }
        targetGroupId={groupId}
      />
    </>
  );
};
